import React, { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Check, Plus, RefreshCw, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import { formStyles } from "../../../components/Inventory-Stocks & Orders/Admin/Products/catalogConstants";
import {
  MasterOption,
  ProductCatalogService,
} from "../../../services/api/productCatalog.service";

export interface CategoryMappingRow {
  hierarchyId: string;
  primaryCategoryId: string;
  primaryCategoryName: string | null;
  subcategoryId: string;
  subcategoryName: string | null;
  groupId: string;
  groupName: string | null;
  isPrimary: boolean;
}

interface ProductCategoryMappingTabProps {
  data: any;
  onChange: (field: string, value: any) => void;
  isEdit: boolean;
  onUpdate?: () => void;
  isLoading?: boolean;
}

/**
 * Files the product under the category tree, then fills in its domain.
 *
 * The three pickers cascade through the hierarchy endpoints rather than the
 * flat masters, so only combinations that exist in Category Mapping can be
 * chosen. `isMedicine` selects which tree is read.
 *
 * Domain and regulatory domain are then derived from the domain rules for the
 * primary mapping - sub category rule first, primary category rule as the
 * fallback, matching the backend's most-specific-wins precedence. They stay
 * editable, because the rules describe what is allowed rather than what must be.
 */
export const ProductCategoryMappingTab: React.FC<
  ProductCategoryMappingTabProps
> = ({ data, onChange, isEdit, onUpdate, isLoading }) => {
  const classificationType = data.isMedicine ? "MEDICINE" : "NON_MEDICINE";
  const mappings: CategoryMappingRow[] = data.categoryMappings || [];

  const [primaryOptions, setPrimaryOptions] = useState<MasterOption[]>([]);
  const [subcategoryOptions, setSubcategoryOptions] = useState<MasterOption[]>(
    []
  );
  const [groupOptions, setGroupOptions] = useState<MasterOption[]>([]);

  const [selectedPrimary, setSelectedPrimary] = useState("");
  const [selectedSubcategory, setSelectedSubcategory] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");
  const [isResolving, setIsResolving] = useState(false);

  const [domainOptions, setDomainOptions] = useState<MasterOption[]>([]);
  const [regulatoryOptions, setRegulatoryOptions] = useState<MasterOption[]>([]);
  const [isOverridingDomain, setIsOverridingDomain] = useState(false);
  const [domainSource, setDomainSource] = useState<
    "SUBCATEGORY" | "PRIMARY" | "NONE" | "MANUAL" | null
  >(null);

  // Primary categories for the active tree.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const res = await ProductCatalogService.getPrimaryCategoryOptions({
        classificationType,
        isActive: true,
      });
      if (!cancelled) setPrimaryOptions(res?.success ? res.data || [] : []);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [classificationType]);

  // Domain pickers, for the override path.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const [domains, regulatory] = await Promise.all([
        ProductCatalogService.getDomainOptions({ isActive: true }),
        ProductCatalogService.getRegulatoryDomainOptions({ isActive: true }),
      ]);
      if (cancelled) return;
      setDomainOptions(domains?.success ? domains.data || [] : []);
      setRegulatoryOptions(regulatory?.success ? regulatory.data || [] : []);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Second cascade level.
  useEffect(() => {
    let cancelled = false;
    setSelectedSubcategory("");
    setSelectedGroup("");
    setGroupOptions([]);
    if (!selectedPrimary) {
      setSubcategoryOptions([]);
      return;
    }
    const load = async () => {
      const res =
        await ProductCatalogService.getSubcategoriesForPrimaryCategory(
          selectedPrimary
        );
      if (!cancelled) setSubcategoryOptions(res?.success ? res.data || [] : []);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [selectedPrimary]);

  // Third cascade level.
  useEffect(() => {
    let cancelled = false;
    setSelectedGroup("");
    if (!selectedPrimary || !selectedSubcategory) {
      setGroupOptions([]);
      return;
    }
    const load = async () => {
      const res = await ProductCatalogService.getGroupsForPrimaryAndSubcategory(
        selectedPrimary,
        selectedSubcategory
      );
      if (!cancelled) setGroupOptions(res?.success ? res.data || [] : []);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [selectedPrimary, selectedSubcategory]);

  /**
   * Reads the domain rules for one category pair, most specific first, and
   * writes the result onto the form.
   */
  const autoFillDomain = useCallback(
    async (primaryCategoryId: string, subcategoryId: string) => {
      if (!primaryCategoryId) return;
      setIsResolving(true);
      try {
        const subRules = await ProductCatalogService.getSubcategoryDomainRules({
          page: 1,
          pageSize: 10,
          primaryCategoryId,
          subcategoryId,
        });
        const subMatch = (subRules?.data || []).find((rule) => rule.isAllowed);
        if (subMatch) {
          onChange("domainId", subMatch.domainId);
          onChange("regulatoryDomainId", subMatch.regulatoryDomainId);
          setDomainSource("SUBCATEGORY");
          return;
        }

        const primaryRules =
          await ProductCatalogService.getPrimaryCategoryDomainRules({
            page: 1,
            pageSize: 10,
            primaryCategoryId,
          });
        const primaryMatch = (primaryRules?.data || []).find(
          (rule) => rule.isAllowed
        );
        if (primaryMatch) {
          onChange("domainId", primaryMatch.domainId);
          onChange("regulatoryDomainId", primaryMatch.regulatoryDomainId);
          setDomainSource("PRIMARY");
          return;
        }

        setDomainSource("NONE");
      } finally {
        setIsResolving(false);
      }
    },
    [onChange]
  );

  const addMapping = async () => {
    if (!selectedPrimary || !selectedSubcategory || !selectedGroup) {
      toast.error("Select a primary category, sub category and product group");
      return;
    }
    if (
      mappings.some(
        (row) =>
          row.primaryCategoryId === selectedPrimary &&
          row.subcategoryId === selectedSubcategory &&
          row.groupId === selectedGroup
      )
    ) {
      toast.error("That combination is already mapped");
      return;
    }

    // The hierarchy row is what the product actually points at, so resolve the
    // three ids to its id before anything is added.
    const res = await ProductCatalogService.getHierarchyOptions({
      classificationType,
      primaryCategoryId: selectedPrimary,
      subcategoryId: selectedSubcategory,
      groupId: selectedGroup,
    });
    const hierarchy = (res?.data || [])[0];
    if (!hierarchy) {
      toast.error(
        "That combination does not exist in Category Mapping. Add it there first."
      );
      return;
    }

    const isFirst = mappings.length === 0;
    const next: CategoryMappingRow[] = [
      ...mappings,
      {
        hierarchyId: hierarchy.id,
        primaryCategoryId: hierarchy.primaryCategoryId,
        primaryCategoryName: hierarchy.primaryCategoryName,
        subcategoryId: hierarchy.subcategoryId,
        subcategoryName: hierarchy.subcategoryName,
        groupId: hierarchy.groupId,
        groupName: hierarchy.groupName,
        isPrimary: isFirst,
      },
    ];
    onChange("categoryMappings", next);

    setSelectedPrimary("");
    if (isFirst) {
      autoFillDomain(hierarchy.primaryCategoryId, hierarchy.subcategoryId);
    }
  };

  const removeMapping = (hierarchyId: string) => {
    const next = mappings.filter((row) => row.hierarchyId !== hierarchyId);
    // Keep exactly one primary - the backend promotes the first otherwise, so
    // make that explicit here instead of letting the two disagree.
    if (next.length > 0 && !next.some((row) => row.isPrimary)) {
      next[0] = { ...next[0], isPrimary: true };
      autoFillDomain(next[0].primaryCategoryId, next[0].subcategoryId);
    }
    onChange("categoryMappings", next);
  };

  const setPrimaryMapping = (hierarchyId: string) => {
    const next = mappings.map((row) => ({
      ...row,
      isPrimary: row.hierarchyId === hierarchyId,
    }));
    onChange("categoryMappings", next);
    const chosen = next.find((row) => row.isPrimary);
    if (chosen) autoFillDomain(chosen.primaryCategoryId, chosen.subcategoryId);
  };

  const primaryMapping = mappings.find((row) => row.isPrimary);
  const domainName =
    domainOptions.find((option) => option.id === data.domainId)?.name || null;
  const regulatoryName =
    regulatoryOptions.find((option) => option.id === data.regulatoryDomainId)
      ?.name || null;

  return (
    <div className="space-y-6">
      {/* ------------------------------------------------- Add a mapping */}
      <section className={formStyles.sectionCard}>
        <h4 className={`${formStyles.sectionTitle} mb-1`}>Category Mapping</h4>
        <p className="text-xs text-gray-500 mb-4">
          Reading the{" "}
          <span className="font-medium">
            {data.isMedicine ? "Medicine" : "Non Medicine"}
          </span>{" "}
          tree, because Is Medicine is set to{" "}
          {data.isMedicine ? "Yes" : "No"} in Basic Details.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div>
            <label className={formStyles.lableStyle}>
              1. Product Category
            </label>
            <select
              value={selectedPrimary}
              onChange={(e) => setSelectedPrimary(e.target.value)}
              className={formStyles.inputStyle}
            >
              <option value="">Select category</option>
              {primaryOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={formStyles.lableStyle}>2. Sub Category</label>
            <select
              value={selectedSubcategory}
              onChange={(e) => setSelectedSubcategory(e.target.value)}
              disabled={!selectedPrimary}
              className={formStyles.inputStyle}
            >
              <option value="">
                {selectedPrimary
                  ? subcategoryOptions.length
                    ? "Select sub category"
                    : "None mapped yet"
                  : "Select a category first"}
              </option>
              {subcategoryOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={formStyles.lableStyle}>3. Product Group</label>
            <select
              value={selectedGroup}
              onChange={(e) => setSelectedGroup(e.target.value)}
              disabled={!selectedSubcategory}
              className={formStyles.inputStyle}
            >
              <option value="">
                {selectedSubcategory
                  ? groupOptions.length
                    ? "Select group"
                    : "None mapped yet"
                  : "Select a sub category first"}
              </option>
              {groupOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          </div>

          <button
            type="button"
            onClick={addMapping}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm h-[42px]"
          >
            <Plus className="w-4 h-4" />
            Add Mapping
          </button>
        </div>
      </section>

      {/* ------------------------------------------------- Current mappings */}
      <section className={formStyles.sectionCard}>
        <h4 className={`${formStyles.sectionTitle} mb-3`}>
          Mapped Categories
          {mappings.length > 0 && (
            <span className="ml-2 text-xs font-normal text-gray-500">
              The primary mapping is the product's breadcrumb and drives its
              domain.
            </span>
          )}
        </h4>

        {mappings.length === 0 ? (
          <p className="text-sm text-gray-500 py-6 text-center border border-dashed border-gray-200 rounded-lg">
            No categories mapped yet.
          </p>
        ) : (
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="w-full text-sm text-left">
              <thead className="text-xs uppercase text-gray-600 bg-gray-50">
                <tr>
                  <th className="px-4 py-3 w-20">Primary</th>
                  <th className="px-4 py-3">Category</th>
                  <th className="px-4 py-3">Sub Category</th>
                  <th className="px-4 py-3">Group</th>
                  <th className="px-4 py-3 w-20">Remove</th>
                </tr>
              </thead>
              <tbody>
                {mappings.map((row) => (
                  <tr
                    key={row.hierarchyId}
                    className="bg-white border-t border-gray-100"
                  >
                    <td className="px-4 py-3">
                      <input
                        type="radio"
                        name="primaryMapping"
                        checked={row.isPrimary}
                        onChange={() => setPrimaryMapping(row.hierarchyId)}
                        className="w-4 h-4 text-emerald-600 focus:ring-emerald-500"
                      />
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-800">
                      {row.primaryCategoryName || row.primaryCategoryId}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {row.subcategoryName || row.subcategoryId}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {row.groupName || row.groupId}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => removeMapping(row.hierarchyId)}
                        className="text-red-500 hover:text-red-700"
                        title="Remove mapping"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ------------------------------------------------- Domain */}
      <section className={formStyles.sectionCard}>
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h4 className={formStyles.sectionTitle}>Domain Mapping</h4>
            <p className="text-xs text-gray-500 mt-0.5">
              Derived from the domain rules for the primary mapping.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {primaryMapping && (
              <button
                type="button"
                onClick={() =>
                  autoFillDomain(
                    primaryMapping.primaryCategoryId,
                    primaryMapping.subcategoryId
                  )
                }
                disabled={isResolving}
                className="flex items-center gap-1.5 text-xs font-medium text-emerald-600 hover:text-emerald-800 disabled:opacity-50"
              >
                <RefreshCw
                  className={`w-3.5 h-3.5 ${isResolving ? "animate-spin" : ""}`}
                />
                Re-resolve
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                setIsOverridingDomain((previous) => !previous);
                if (!isOverridingDomain) setDomainSource("MANUAL");
              }}
              className="text-xs font-medium text-blue-600 hover:text-blue-800"
            >
              {isOverridingDomain ? "Use derived values" : "Override manually"}
            </button>
          </div>
        </div>

        {!primaryMapping && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 text-amber-800 mb-4">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <p className="text-xs">
              Map a category above and the domain will be filled in from its
              rules.
            </p>
          </div>
        )}

        {primaryMapping && domainSource === "NONE" && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 text-amber-800 mb-4">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <p className="text-xs">
              No domain rule exists for{" "}
              <span className="font-medium">
                {primaryMapping.primaryCategoryName}
                {primaryMapping.subcategoryName
                  ? ` / ${primaryMapping.subcategoryName}`
                  : ""}
              </span>
              . Add one under Domain Mapping, or set the domain manually.
            </p>
          </div>
        )}

        {primaryMapping &&
          (domainSource === "SUBCATEGORY" || domainSource === "PRIMARY") && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-emerald-50 text-emerald-800 mb-4">
              <Check className="w-4 h-4 mt-0.5 shrink-0" />
              <p className="text-xs">
                Auto-filled from the{" "}
                {domainSource === "SUBCATEGORY"
                  ? "sub category"
                  : "primary category"}{" "}
                rule.
              </p>
            </div>
          )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={formStyles.lableStyle}>Domain</label>
            {isOverridingDomain ? (
              <select
                value={data.domainId}
                onChange={(e) => onChange("domainId", e.target.value)}
                className={formStyles.inputStyle}
              >
                <option value="">Select domain</option>
                {domainOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                readOnly
                value={domainName || ""}
                placeholder="Not resolved"
                className={formStyles.inputStyle}
              />
            )}
          </div>

          <div>
            <label className={formStyles.lableStyle}>Regulatory Domain</label>
            {isOverridingDomain ? (
              <select
                value={data.regulatoryDomainId}
                onChange={(e) =>
                  onChange("regulatoryDomainId", e.target.value)
                }
                className={formStyles.inputStyle}
              >
                <option value="">Select regulatory domain</option>
                {regulatoryOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                readOnly
                value={regulatoryName || ""}
                placeholder="Not resolved"
                className={formStyles.inputStyle}
              />
            )}
          </div>
        </div>
      </section>

      {isEdit && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onUpdate}
            disabled={isLoading}
            className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition disabled:opacity-50"
          >
            {isLoading ? "Updating..." : "Update Category & Domain"}
          </button>
        </div>
      )}
    </div>
  );
};
